import { Service } from '@toeverything/infra';

import { registerCoreViewBackedAppDescriptors } from './descriptors';
import type { ViewBackedAppDescriptor } from './types';

export class ViewBackedAppDescriptorRegistry {
  private readonly descriptorsByAppKind = new Map<
    string,
    ViewBackedAppDescriptor
  >();

  private readonly descriptorsByViewMode = new Map<
    string,
    ViewBackedAppDescriptor[]
  >();

  constructor(descriptors: readonly ViewBackedAppDescriptor[]) {
    this.assertUnique(descriptors, descriptor => descriptor.appKind, 'appKind');
    this.assertUnique(
      descriptors,
      descriptor => descriptor.viewMode,
      'viewMode'
    );
    this.assertUnique(
      descriptors,
      descriptor => descriptor.resourcePrefix,
      'resourcePrefix'
    );
    for (const descriptor of descriptors) {
      this.descriptorsByAppKind.set(descriptor.appKind, descriptor);
      const byMode = this.descriptorsByViewMode.get(descriptor.viewMode) ?? [];
      byMode.push(descriptor);
      this.descriptorsByViewMode.set(descriptor.viewMode, byMode);
    }
  }

  get(appKind: string): ViewBackedAppDescriptor {
    const descriptor = this.descriptorsByAppKind.get(appKind);
    if (!descriptor) {
      throw new ViewBackedAppDescriptorRegistryError(
        `Unknown view-backed app kind "${appKind}"`
      );
    }
    return descriptor;
  }

  getByViewMode(viewMode: string): readonly ViewBackedAppDescriptor[] {
    const descriptors = this.descriptorsByViewMode.get(viewMode);
    if (!descriptors) {
      throw new ViewBackedAppDescriptorRegistryError(
        `Unknown view-backed app view mode "${viewMode}"`
      );
    }
    return descriptors;
  }

  list(): readonly ViewBackedAppDescriptor[] {
    return [...this.descriptorsByAppKind.values()];
  }

  private assertUnique(
    descriptors: readonly ViewBackedAppDescriptor[],
    key: (descriptor: ViewBackedAppDescriptor) => string,
    label: string
  ): void {
    const seen = new Set<string>();
    for (const descriptor of descriptors) {
      const value = key(descriptor);
      if (seen.has(value)) {
        throw new ViewBackedAppDescriptorRegistryError(
          `Duplicate view-backed app descriptor ${label}: ${value}`
        );
      }
      seen.add(value);
    }
  }
}

export class ViewBackedAppRegistryService extends Service {
  private readonly descriptors = new Set<ViewBackedAppDescriptor>();
  private readonly descriptorListeners = new Set<
    (descriptor: ViewBackedAppDescriptor) => void
  >();

  constructor() {
    super();
    registerCoreViewBackedAppDescriptors(this);
  }

  registerDescriptor(descriptor: ViewBackedAppDescriptor): void {
    const existing = [...this.descriptors].find(
      candidate => candidate.appKind === descriptor.appKind
    );
    if (existing === descriptor) return;
    const next = new Set(this.descriptors);
    next.add(descriptor);
    new ViewBackedAppDescriptorRegistry([...next]);
    this.descriptors.add(descriptor);
    for (const listener of this.descriptorListeners) {
      listener(descriptor);
    }
  }

  onDescriptorRegistered(
    listener: (descriptor: ViewBackedAppDescriptor) => void
  ): () => void {
    for (const descriptor of this.list()) {
      listener(descriptor);
    }
    this.descriptorListeners.add(listener);
    return () => this.descriptorListeners.delete(listener);
  }

  get(appKind: string): ViewBackedAppDescriptor {
    return this.registry().get(appKind);
  }

  getByViewMode(viewMode: string): readonly ViewBackedAppDescriptor[] {
    return this.registry().getByViewMode(viewMode);
  }

  list(): readonly ViewBackedAppDescriptor[] {
    return this.registry().list();
  }

  private registry(): ViewBackedAppDescriptorRegistry {
    return new ViewBackedAppDescriptorRegistry([...this.descriptors]);
  }
}

export class ViewBackedAppDescriptorRegistryError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'ViewBackedAppDescriptorRegistryError';
  }
}
